/**
 * Showing a board's shipped columns and status options in the reader's
 * language, without rewriting what the board has stored.
 *
 * A template or a starter layout writes its labels in as board data, so a board
 * created before a locale existed still says "Due date" to everyone. Resolving
 * the label from the column's type instead is not open to us: the label is
 * editable, and a board that has been curated for a year must keep the words
 * its team chose.
 *
 * So the rewrite is applied to what is rendered and never to what is saved.
 * Formula columns are why that distinction has to hold rather than merely being
 * tidy: a formula names its operands by label in stored text (`{Budget} * 0.2`),
 * so a label that differed per reader would evaluate differently per reader. The
 * stored array stays the single thing formulas, persistence and renames agree
 * on, and only the view layer sees the translated copy.
 */
import type { ParseKeys, TFunction } from 'i18next';

import { i18n } from '@/shared/i18n';
import { SUPPORTED_LOCALE_CODES } from '@/shared/i18n/locales';

import type { CustomColumn, StatusOption } from './board-columns';

/**
 * The reader's translator, taken rather than reached for. A caller that holds
 * it in a `useMemo` dependency list recomputes when the language changes, which
 * is the one thing reading the `i18n` singleton here would not give them.
 */
type Translate = TFunction<'trackers'>;

/**
 * Where each shipped column id took its label from. Ids are stable across every
 * board that ever seeded one, which is what makes them usable as the question
 * "is this a column we wrote?" years after the fact.
 *
 * The `starter_*` ids are seeded by both board engines from different hosts
 * (`useTrackersBoard` for briefs, `customTrackerBoard` for free form). One entry
 * each is enough only because the two render identically in every locale, which
 * `shipped-labels.test.ts` is what keeps true.
 */
const COLUMN_LABEL_KEY: Partial<Record<string, ParseKeys<'trackers'>>> = {
  assignee: 'trackerTemplate.builtinTemplates.assignee',
  budget: 'trackerTemplate.builtinTemplates.budget',
  channel: 'trackerTemplate.builtinTemplates.channel',
  due_date: 'trackerTemplate.builtinTemplates.dueDate',
  owner: 'trackerTemplate.builtinTemplates.owner',
  points: 'trackerTemplate.builtinTemplates.storyPoints',
  priority: 'trackerTemplate.builtinTemplates.priority',
  publish_date: 'trackerTemplate.builtinTemplates.publishDate',
  reported: 'trackerTemplate.builtinTemplates.reported',
  starter_assets: 'trackersBoard.useTrackersBoard.projectAssets',
  starter_brief_type: 'trackersBoard.useTrackersBoard.briefType',
  starter_due_date: 'trackersBoard.useTrackersBoard.dueDate',
  starter_notes: 'trackersBoard.useTrackersBoard.notes',
  starter_owner: 'trackersBoard.useTrackersBoard.owner',
  starter_status: 'trackersBoard.useTrackersBoard.status',
  status: 'trackerTemplate.builtinTemplates.status',
};

/** The same, for the options inside a shipped status column. */
const STATUS_OPTION_LABEL_KEY: Partial<Record<string, ParseKeys<'trackers'>>> = {
  done: 'trackersBoard.useTrackersBoard.done',
  high: 'trackerTemplate.builtinTemplates.high',
  in_progress: 'trackerTemplate.builtinTemplates.inProgress',
  in_review: 'trackerTemplate.builtinTemplates.inReview',
  low: 'trackerTemplate.builtinTemplates.low',
  medium: 'trackerTemplate.builtinTemplates.medium',
  not_started: 'trackerTemplate.builtinTemplates.notStarted',
  stuck: 'trackersBoard.useTrackersBoard.stuck',
  working_on_it: 'trackersBoard.useTrackersBoard.workingOnIt',
};

// eslint-disable-next-line i18next/no-literal-string -- the i18next namespace id
const NAMESPACE = 'trackers';

/**
 * Every rendering of `key` we can still recognise. English is always among them
 * because it is the only catalogue loaded at boot; another locale joins once its
 * chunk has been fetched, which it has for anyone who has read the board in it.
 *
 * A locale we cannot resolve is a miss, and a miss leaves the stored label
 * alone. That is the right way to fail: the words on screen stay the ones the
 * board holds, rather than a guess at whether somebody chose them.
 */
function knownRenderings(key: ParseKeys<'trackers'>): string[] {
  return SUPPORTED_LOCALE_CODES.filter((code) => i18n.hasResourceBundle(code, NAMESPACE)).map(
    (code) => i18n.getFixedT(code, NAMESPACE)(key),
  );
}

/**
 * The reader's wording for a label the platform seeded, or the label untouched.
 *
 * Matching the stored text against what we would have written is the closest a
 * layout gets to recording "nobody has renamed this", since it records no such
 * thing. The limit that follows is the same one the assets header migration
 * carries: someone who renames a column to exactly its shipped wording is
 * indistinguishable from someone who never renamed it, and sees it follow the
 * language.
 */
function readerLabel(
  id: string,
  stored: string,
  keys: Partial<Record<string, ParseKeys<'trackers'>>>,
  t: Translate,
): string {
  const key = keys[id];
  if (!key) return stored;
  if (!knownRenderings(key).includes(stored)) return stored;
  return t(key);
}

/**
 * A copy of `columns` for rendering. Never persist it, and never evaluate a
 * formula against it.
 */
export function toReaderColumns(columns: CustomColumn[], t: Translate): CustomColumn[] {
  return columns.map((column) => ({
    ...column,
    label: readerLabel(column.id, column.label, COLUMN_LABEL_KEY, t),
    ...(column.statusOptions ? { statusOptions: toReaderOptions(column.statusOptions, t) } : {}),
  }));
}

/** The options of one status column, for rendering. Same caveat as above. */
export function toReaderOptions(options: StatusOption[], t: Translate): StatusOption[] {
  return options.map((option) => ({
    ...option,
    label: readerLabel(option.id, option.label, STATUS_OPTION_LABEL_KEY, t),
  }));
}

/**
 * Whether `next` is no more than the header the reader was already shown, which
 * is what an inline rename hands back when it is opened and dismissed without
 * an edit. Callers treat that as no rename, since writing it would translate
 * the stored label for the whole workspace on a keystroke nobody made.
 */
export function isReaderLabel(column: CustomColumn, next: string, t: Translate): boolean {
  return readerLabel(column.id, column.label, COLUMN_LABEL_KEY, t) === next;
}

/**
 * Options on their way back from the status editor, with every label the user
 * did not actually retype restored to the one the board stores.
 *
 * The editor is seeded from the reader's copy, so without this, opening it to
 * add one option and pressing save would persist the reader's wording for all
 * the others. On a board a whole workspace shares, that is the first person to
 * touch it choosing the language for everyone, from an action that did not look
 * like renaming anything.
 */
export function withStoredLabels(
  stored: readonly StatusOption[],
  edited: StatusOption[],
  t: Translate,
): StatusOption[] {
  const before = new Map(stored.map((option) => [option.id, option]));
  return edited.map((option) => {
    const previous = before.get(option.id);
    if (!previous) return option;
    const untouched =
      readerLabel(previous.id, previous.label, STATUS_OPTION_LABEL_KEY, t) === option.label;
    return untouched ? { ...option, label: previous.label } : option;
  });
}

/** Ids the tables above answer for, so a test can hold them to the shipped set. */
export const SHIPPED_COLUMN_IDS = Object.keys(COLUMN_LABEL_KEY);
export const SHIPPED_STATUS_OPTION_IDS = Object.keys(STATUS_OPTION_LABEL_KEY);
